import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { liveScoreAPI } from '../services/api';

function LiveMatchCard({ match }) {
  const team1Score = match.team1?.score;
  const team2Score = match.team2?.score;
  const isCompleted = match.status === 'COMPLETED' || match.match_result;

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${isCompleted ? 'border-green-500' : 'border-red-500'}`}>
      <div className="flex justify-between items-start mb-4">
        {isCompleted ? (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
            COMPLETED
          </span>
        ) : (
          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded animate-pulse">
            LIVE
          </span>
        )}
        <span className="text-sm text-gray-500">{match.format}</span>
      </div>

      <div className="space-y-4">
        <div className={`flex justify-between items-center ${team1Score?.is_batting ? 'font-bold' : ''}`}>
          <span className="text-lg">{match.team1.name}</span>
          {team1Score && (
            <span className="text-xl">
              {team1Score.runs}/{team1Score.wickets}
              <span className="text-sm text-gray-500 ml-1">({team1Score.overs})</span>
            </span>
          )}
        </div>

        <div className={`flex justify-between items-center ${team2Score?.is_batting ? 'font-bold' : ''}`}>
          <span className="text-lg">{match.team2.name}</span>
          {team2Score && (
            <span className="text-xl">
              {team2Score.runs}/{team2Score.wickets}
              <span className="text-sm text-gray-500 ml-1">({team2Score.overs})</span>
            </span>
          )}
        </div>
      </div>

      {match.current_batsmen && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-gray-500 mb-2">Batting:</p>
          <div className="space-y-1">
            {match.current_batsmen.map((batsman, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className={batsman.is_striker ? 'font-semibold' : ''}>
                  {batsman.player_name}
                  {batsman.is_striker && ' *'}
                </span>
                <span>{batsman.runs} ({batsman.balls})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {match.current_bowler && (
        <div className="mt-2">
          <p className="text-sm text-gray-500">Bowling: {match.current_bowler.player_name} {match.current_bowler.wickets}/{match.current_bowler.runs} ({match.current_bowler.overs})</p>
        </div>
      )}

      {match.chase_info && (
        <div className="mt-4 pt-4 border-t bg-blue-50 -mx-6 -mb-6 px-6 py-3 rounded-b-lg">
          <p className="text-center font-medium text-blue-800">
            Need {match.chase_info.runs_needed} runs from {match.chase_info.balls_remaining} balls
          </p>
          <p className="text-center text-sm text-blue-600">
            RRR: {match.chase_info.required_run_rate?.toFixed(2)}
          </p>
        </div>
      )}

      {match.current_over && match.current_over.length > 0 && !isCompleted && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-gray-500 mb-2">This over:</p>
          <div className="flex space-x-2">
            {match.current_over.map((ball, idx) => (
              <span
                key={idx}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${ball.is_wicket ? 'bg-red-500 text-white' :
                    ball.is_boundary ? 'bg-green-500 text-white' :
                    ball.is_wide || ball.is_no_ball ? 'bg-yellow-400' :
                    'bg-gray-200'}`}
              >
                {ball.is_wicket ? 'W' :
                 ball.is_wide ? 'Wd' :
                 ball.is_no_ball ? 'Nb' :
                 ball.runs_scored}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Match Result */}
      {match.match_result && (
        <div className="mt-4 pt-4 border-t bg-green-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
          <p className="text-center font-bold text-green-800 text-lg">
            {match.match_result.winner_id === match.team1.id
              ? match.team1.name
              : match.match_result.winner_id === match.team2.id
                ? match.team2.name
                : ''} {match.match_result.result_description}
          </p>
          {match.man_of_the_match && (
            <p className="text-center text-sm text-green-600 mt-1">
              Man of the Match: {match.man_of_the_match.name || match.man_of_the_match}
            </p>
          )}
        </div>
      )}

      {!match.match_result && (
        <div className="mt-4 pt-4 border-t text-sm text-gray-500">
          {match.venue}
        </div>
      )}
    </div>
  );
}

function UpcomingMatchCard({ match }) {
  // Append 'Z' to indicate UTC so browser converts to local timezone
  const matchDate = match.match_date ? new Date(match.match_date.endsWith('Z') ? match.match_date : match.match_date + 'Z') : null;
  const isValidDate = matchDate && !isNaN(matchDate.getTime());

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex justify-between items-start mb-2">
        <span className="text-xs text-gray-500">{match.tournament?.name}</span>
        <span className="text-xs text-gray-500">{match.format}</span>
      </div>

      <div className="flex justify-between items-center py-2">
        <span className="font-medium">{match.team1.name}</span>
        <span className="text-gray-400">vs</span>
        <span className="font-medium">{match.team2.name}</span>
      </div>

      <div className="text-sm text-gray-500 text-center">
        {isValidDate
          ? `${matchDate.toLocaleDateString()} at ${matchDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : 'Date TBD'}
      </div>
      <div className="text-xs text-gray-400 text-center mt-1">{match.venue}</div>
    </div>
  );
}

function RecentMatchCard({ match }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex justify-between items-start mb-2">
        <span className="text-xs text-gray-500">{match.tournament?.name}</span>
        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Completed</span>
      </div>

      <div className="space-y-2 py-2">
        <div className={`flex justify-between ${match.winner === match.team1.id ? 'font-bold' : ''}`}>
          <span>{match.team1.name}</span>
          {match.team1.score && (
            <span>{match.team1.score.runs}/{match.team1.score.wickets}</span>
          )}
        </div>
        <div className={`flex justify-between ${match.winner === match.team2.id ? 'font-bold' : ''}`}>
          <span>{match.team2.name}</span>
          {match.team2.score && (
            <span>{match.team2.score.runs}/{match.team2.score.wickets}</span>
          )}
        </div>
      </div>

      {match.result && (
        <div className="text-sm text-green-600 font-medium text-center pt-2 border-t">
          {match.winner === match.team1.id
            ? match.team1.name
            : match.winner === match.team2.id
              ? match.team2.name
              : ''} {match.result}
        </div>
      )}

      {match.man_of_the_match && (
        <div className="text-xs text-gray-500 text-center mt-1">
          MoM: {match.man_of_the_match.name}
        </div>
      )}
    </div>
  );
}

export default function PublicLiveScore() {
  const [activeTab, setActiveTab] = useState('live');

  const { data: liveMatches = [], refetch: refetchLive } = useQuery({
    queryKey: ['liveMatches'],
    queryFn: liveScoreAPI.getLiveMatches,
    refetchInterval: 10000, // Refresh every 10 seconds for public view
  });

  const { data: todaysMatches = [] } = useQuery({
    queryKey: ['todaysMatches'],
    queryFn: liveScoreAPI.getTodaysMatches,
  });

  const { data: upcomingMatches = [] } = useQuery({
    queryKey: ['upcomingMatches'],
    queryFn: () => liveScoreAPI.getUpcomingMatches(7),
  });

  const { data: recentMatches = [] } = useQuery({
    queryKey: ['recentMatches'],
    queryFn: () => liveScoreAPI.getRecentMatches(7),
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'live') {
        refetchLive();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTab, refetchLive]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-center space-x-2">
            <img src="/logo.png" alt="Innings Pro" className="h-12 w-24" />
            <h1 className="text-base sm:text-xl font-bold text-gray-800">Innings Pro - Live Scores</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex justify-center mb-6 px-2">
          <div className="flex flex-wrap justify-center gap-1 bg-gray-100 p-1 rounded-lg w-full sm:w-auto">
            <button
              onClick={() => setActiveTab('live')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                activeTab === 'live' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Live {liveMatches.length > 0 && `(${liveMatches.length})`}
            </button>
            <button
              onClick={() => setActiveTab('today')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                activeTab === 'today' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Today {todaysMatches.length > 0 && `(${todaysMatches.length})`}
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                activeTab === 'upcoming' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setActiveTab('recent')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                activeTab === 'recent' ? 'bg-white shadow-sm text-green-600' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Recent
            </button>
          </div>
        </div>

        {/* Live Matches */}
        {activeTab === 'live' && (
          <div>
            {liveMatches.length === 0 ? (
              <div className="bg-white rounded-lg p-12 text-center shadow-sm">
                <img src="/logo.png" alt="Innings Pro" className="h-24 w-48 mx-auto mb-4 opacity-30" />
                <p className="text-gray-500 text-lg">No live matches at the moment</p>
                <p className="text-gray-400 text-sm mt-2">Check back later for live updates</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {liveMatches.map((match) => (
                  <LiveMatchCard key={match.match_id} match={match} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Today's Matches */}
        {activeTab === 'today' && (
          <div>
            {todaysMatches.length === 0 ? (
              <div className="bg-white rounded-lg p-12 text-center shadow-sm">
                <p className="text-gray-500">No matches scheduled for today</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {todaysMatches.map((match) => (
                  <UpcomingMatchCard key={match.match_id} match={match} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upcoming Matches */}
        {activeTab === 'upcoming' && (
          <div>
            {upcomingMatches.length === 0 ? (
              <div className="bg-white rounded-lg p-12 text-center shadow-sm">
                <p className="text-gray-500">No upcoming matches</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcomingMatches.map((match) => (
                  <UpcomingMatchCard key={match.match_id} match={match} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent Matches */}
        {activeTab === 'recent' && (
          <div>
            {recentMatches.length === 0 ? (
              <div className="bg-white rounded-lg p-12 text-center shadow-sm">
                <p className="text-gray-500">No recent matches</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentMatches.map((match) => (
                  <RecentMatchCard key={match.match_id} match={match} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Auto-refresh indicator */}
        <div className="text-center mt-8 text-xs text-gray-400">
          Auto-refreshing every 10 seconds
        </div>
      </main>
    </div>
  );
}
